import PropTypes from 'prop-types';
import {
	Paper,
	Box,
	Typography,
	FormControl,
	Select,
	MenuItem,
	Tooltip,
	CircularProgress,
	Alert,
} from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

const MicrosoftFolderDropdown = ({
	folders,
	selectedFolder,
	onFolderChange,
	isLoading,
	error,
	disabled,
	emailDirection,
}) => {
	const getDefaultFolder = () => {
		switch (emailDirection) {
			case 'received':
				return 'Inbox (default)';
			case 'sent':
				return 'Sent Items (default)';
			case 'received_and_sent':
				return 'Inbox & Sent Items (default)';
			default:
				return 'All emails (no folder filter)';
		}
	};

	return (
		<Paper
			variant='outlined'
			sx={{
				px: 3,
				py: 2,
				mb: 2,
				borderRadius: 0.8,
				backgroundColor: '#f9fbfc',
			}}
		>
			<Box sx={{ mb: 2 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
					<Typography
						variant='subtitle1'
						sx={{ fontWeight: 600, mb: 0.1, fontSize: '0.9rem' }}
					>
						Microsoft Folder Selection
					</Typography>

					<Tooltip
						title='Select a specific folder to sync emails from. Only emails in the selected folder and its subfolders will be synced to your CRM.'
						placement='top'
						arrow
						slotProps={{
							tooltip: {
								sx: {
									borderRadius: '5px',
								},
							},
						}}
					>
						<InfoOutlined
							sx={{
								fontSize: '1rem',
								color: 'text.secondary',
								cursor: 'help',
								mb: 0.3,
							}}
						/>
					</Tooltip>
				</Box>
				<Typography
					variant='body2'
					color='text.secondary'
					sx={{ fontSize: '0.8rem' }}
				>
					Choose which Outlook folder to sync (optional)
				</Typography>
			</Box>

			{error && (
				<Alert severity='error' sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			<FormControl
				fullWidth
				sx={{ backgroundColor: 'white' }}
				disabled={disabled || isLoading}
			>
				<Select
					value={selectedFolder || ''}
					onChange={(e) => onFolderChange(e.target.value)}
					displayEmpty
					endAdornment={
						isLoading && <CircularProgress color='inherit' size={20} />
					}
					renderValue={(value) => {
						if (value === '') {
							return getDefaultFolder();
						}
						const selectedFolderObj = folders.find((f) => f.id === value);
						return selectedFolderObj?.name || '';
					}}
					MenuProps={{
						PaperProps: {
							sx: {
								maxWidth: 0,
							},
						},
						MenuListProps: {
							sx: {
								maxHeight: 500,
								overflow: 'auto',
								scrollbarWidth: 'thin',
							},
						},
					}}
				>
					<MenuItem value=''>{getDefaultFolder()}</MenuItem>
					{folders.map((folder) => (
						<MenuItem
							key={folder.id}
							value={folder.id}
							title={folder.name}
							sx={{
								width: '100%',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whitespace: 'nowrap',
								display: 'block',
							}}
						>
							{folder.name}
						</MenuItem>
					))}
				</Select>
			</FormControl>
		</Paper>
	);
};

MicrosoftFolderDropdown.propTypes = {
	folders: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			name: PropTypes.string.isRequired,
		})
	),
	selectedFolder: PropTypes.string,
	onFolderChange: PropTypes.func.isRequired,
	isLoading: PropTypes.bool,
	error: PropTypes.string,
	disabled: PropTypes.bool,
	emailDirection: PropTypes.oneOf(['received', 'sent', 'received_and_sent']),
};

MicrosoftFolderDropdown.defaultProps = {
	folders: [],
	selectedFolder: '',
	isLoading: false,
	error: null,
	disabled: false,
	emailDirection: 'received_and_sent',
};

export default MicrosoftFolderDropdown;
